import * as React from 'react';
import { clsx } from 'clsx';
import { createLocators } from '@u1/locators';
import Step, { IStepProps } from './Step/Step';

import useStyles from './Stepper.styles';

export interface IStepperProps {
  /**
   * Steps to render
   */
  steps: Pick<IStepProps, 'title' | 'description'>[];
  /**
   * Steps alignment
   */
  variant: IStepProps['variant'];
  /**
   * Indicates active step number. All steps prior to this will be highlighted and marked as completed
   */
  activeStep: number;
  /**
   * Add completion checkmark if active step is completed
   */
  isActiveStepCompleted: boolean;
}

export const stepperLocators = createLocators({
  name: 'Stepper',
  componentLocators: ['container'],
});

const Stepper: React.FC<IStepperProps> = ({
  steps = [],

  variant = 'horizontal',
  activeStep,
  isActiveStepCompleted,
}) => {
  const { locators } = stepperLocators();
  const classes = useStyles();

  const stepsToRender = React.useMemo(() => {
    const activeStepIndex = activeStep - 1;
    return steps.slice(0, 9).map((step, idx) => ({
      ...step,
      highlighted: idx <= activeStepIndex,
      index: idx,
      completed:
        idx < activeStepIndex ||
        (idx === activeStepIndex && isActiveStepCompleted),
    }));
  }, [steps.length, activeStep, isActiveStepCompleted]);

  return (
    <div
      {...locators.container()}
      className={clsx(classes.root, {
        [classes.vertical]: variant === 'vertical',
      })}
    >
      {stepsToRender.map((step) => (
        <Step {...step} key={step.title} variant={variant} />
      ))}
    </div>
  );
};

export default Stepper;
